import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, FileText, Calculator, Users, Globe, Building2, Briefcase,
  CheckCircle2, Shield, Clock, Award, ChevronRight, Phone
} from 'lucide-react';

export default function ServicesPage() {
  const navigate = useNavigate();

  const serviceCategories = [
    {
      icon: FileText,
      title: 'Tax Filing',
      description: 'Complete tax filing and compliance for individuals and businesses.',
      color: 'blue',
      services: [
        { name: 'Individual Tax Returns (1040)', desc: 'Expert preparation of personal tax returns' },
        { name: 'Business Tax Returns (1120/1120S)', desc: 'Corporate and S-Corp tax compliance' },
        { name: 'Partnership Returns (1065)', desc: 'Multi-member LLC and partnership filings' },
        { name: 'Tax Planning & Strategy', desc: 'Year-round advisory for tax optimization' },
        { name: 'IRS Support', desc: 'Audit support and dispute resolution' },
        { name: 'Amendment Filing', desc: 'Correcting previous returns' }
      ],
      path: '/login'
    },
    {
      icon: Building2,
      title: 'LLC Formation',
      description: 'Professional business formation services to get your company started right.',
      color: 'green',
      services: [
        { name: 'LLC Registration', desc: 'File with your state and get approved' },
        { name: 'EIN Application', desc: 'Get your federal tax ID number' },
        { name: 'Operating Agreement', desc: 'Professional LLC operating agreement' },
        { name: 'Business License', desc: 'Required permits and licenses' },
        { name: 'Registered Agent', desc: 'Official business address service' },
        { name: 'Compliance Calendar', desc: 'Never miss important deadlines' }
      ],
      path: '/login'
    },
    {
      icon: Calculator,
      title: 'Bookkeeping',
      description: 'Professional bookkeeping services to keep your finances organized.',
      color: 'purple',
      services: [
        { name: 'Monthly Bookkeeping', desc: 'Transaction recording and categorization' },
        { name: 'Financial Statements', desc: 'Balance sheet, P&L, cash flow reports' },
        { name: 'Bank Reconciliation', desc: 'Accurate account matching' },
        { name: 'Expense Tracking', desc: 'Organize deductible expenses' },
        { name: 'Quarterly Reports', desc: 'Regular financial updates' },
        { name: 'Year-End Prep', desc: 'Get ready for tax season' }
      ],
      path: '/login'
    }
  ];

  const benefits = [
    { icon: Shield, title: '100% Accurate', desc: 'Guaranteed accuracy or we pay the penalties' },
    { icon: Clock, title: 'Fast & Easy', desc: 'File your taxes in under 30 minutes' },
    { icon: Award, title: 'Maximum Refund', desc: 'Get every dollar you deserve back' },
    { icon: CheckCircle2, title: 'Expert Help', desc: 'Real tax pros available when you need them' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-200',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 border-pink-200'
    };
    return colors[color] || colors.blue;
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1664382951020-41874ae61a44?w=1920')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-500/20 text-blue-200 border-blue-400/30">
              Full-Service Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              What We Offer
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Tax filing, LLC formation, and bookkeeping services to help you succeed.
            </p>
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50" onClick={() => navigate('/contact')}>
              <Phone className="mr-2 h-5 w-5" /> Get Help
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-gray-50 dark:bg-gray-800 border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{benefit.title}</div>
                    <div className="text-xs text-muted-foreground">{benefit.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        {/* Service Categories */}
        <div className="space-y-16">
          {serviceCategories.map((category, index) => {
            const Icon = category.icon;
            const colorClasses = getColorClasses(category.color);
            const isEven = index % 2 === 0;
            
            return (
              <section key={index} className={`${!isEven ? 'bg-gray-50 dark:bg-gray-800/50 -mx-4 px-4 py-12 rounded-3xl' : ''}`}>
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${colorClasses} border mb-6`}>
                      <Icon className="h-5 w-5" />
                      <span className="font-semibold">{category.title}</span>
                    </div>
                    <h2 className="text-3xl font-bold mb-4">{category.title}</h2>
                    <p className="text-lg text-muted-foreground mb-6">{category.description}</p>
                    <Button 
                      size="lg"
                      onClick={() => navigate(category.path)}
                    >
                      Get Started <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <Card className="border-2">
                      <CardHeader>
                        <CardTitle className="text-lg">Services Included</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {category.services.map((service, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium">{service.name}</div>
                              <div className="text-sm text-muted-foreground">{service.desc}</div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Pricing CTA */}
        <section className="mt-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
              <p className="text-blue-100 text-lg mb-6">
                No hidden fees, no surprises. Get clear pricing tailored to your specific needs.
              </p>
              <Button size="lg" variant="secondary" onClick={() => navigate('/pricing')}>
                View Pricing <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-white">
              <h3 className="text-xl font-semibold mb-4">Popular Packages</h3>
              <ul className="space-y-3">
                <li className="flex justify-between">
                  <span>Individual Tax Filing</span>
                  <span className="font-bold">From $99</span>
                </li>
                <li className="flex justify-between">
                  <span>Business Tax Filing</span>
                  <span className="font-bold">From $499</span>
                </li>
                <li className="flex justify-between">
                  <span>Monthly Bookkeeping</span>
                  <span className="font-bold">From $299/mo</span>
                </li>
                <li className="flex justify-between">
                  <span>LLC Formation</span>
                  <span className="font-bold">From $149</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ CTA */}
        <section className="mt-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Not Sure Which Service You Need?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Book a free consultation with our experts. We'll analyze your situation and 
            recommend the right services for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/contact')}>
              Book Free Consultation
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/resources')}>
              View FAQs
            </Button>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}